import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { parseISO, format, subMonths, startOfMonth, isAfter, isSameMonth } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { providersAPI } from '../../api/providers';
import { bookingsAPI } from '../../api/bookings';
import Avatar from '../../components/common/Avatar';
import Spinner from '../../components/common/Spinner';
import { formatCurrency } from '../../utils/formatter';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  StarIcon,
  ArrowRightIcon,
  ClockIcon,
  MapPinIcon,
  ChartBarIcon,
  ScissorsIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const UPCOMING_STATUSES = new Set(['PENDING', 'CONFIRMED', 'ACCEPTED', 'PAID']);
const COMPLETED_STATUSES = new Set(['COMPLETED']);
const CANCELLED_STATUSES = new Set(['CANCELLED', 'DECLINED', 'REFUNDED', 'NO_SHOW']);

const STATUS_COLORS = {
  Completed: '#22c55e',
  Upcoming: 'var(--orange-9)',
  Cancelled: '#e5484d',
};

const earningsAmount = (b) => {
  const subtotal = Number(b.subtotal || b.totalPrice || b.price || 0);
  const commission = Number(b.commission || 0);
  return Math.max(0, subtotal - commission);
};
const bookingDate = (b) => {
  const raw = b.startAt || b.scheduledAt || b.date || b.createdAt;
  if (!raw) return null;
  try { return typeof raw === 'string' ? parseISO(raw) : new Date(raw); }
  catch { return null; }
};

const Card = ({ children, style, ...rest }) => (
  <div
    {...rest}
    style={{
      backgroundColor: '#fff',
      border: '1px solid var(--gray-4)',
      borderRadius: '14px',
      padding: '20px',
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, children, action }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      {Icon && <Icon style={{ width: '18px', height: '18px', color: 'var(--orange-9)' }} />}
      <h3
        style={{
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--gray-12)',
          margin: 0,
        }}
      >
        {children}
      </h3>
    </div>
    {action}
  </div>
);

const StatCard = ({ label, value, sub, icon: Icon, tint, iconColor }) => (
  <Card style={{ padding: 'clamp(10px, 2.5vw, 14px)' }}>
    <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-3">
      <div
        style={{
          width: 'clamp(26px, 6vw, 32px)',
          height: 'clamp(26px, 6vw, 32px)',
          borderRadius: '8px',
          backgroundColor: tint,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon style={{ width: 'clamp(13px, 3.2vw, 16px)', height: 'clamp(13px, 3.2vw, 16px)', color: iconColor }} />
      </div>
      <span style={{ fontSize: 'clamp(11px, 2.8vw, 12px)', color: 'var(--gray-10)' }}>{label}</span>
    </div>
    <p
      style={{
        fontSize: 'clamp(17px, 4.5vw, 21px)',
        fontWeight: 700,
        color: 'var(--gray-12)',
        margin: 0,
        textAlign: 'left',
        lineHeight: 1.1,
      }}
    >
      {value}
    </p>
    {sub && (
      <p style={{ fontSize: 'clamp(10px, 2.6vw, 11px)', color: 'var(--gray-10)', margin: '3px 0 0', textAlign: 'left' }}>
        {sub}
      </p>
    )}
  </Card>
);

// SVG bar chart: monthly earnings
const EarningsChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.value));
  const width = 520;
  const height = 180;
  const padX = 28;
  const padTop = 16;
  const padBottom = 36;
  const usableW = width - padX * 2;
  const usableH = height - padTop - padBottom;
  const barW = usableW / data.length - 14;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {[0.25, 0.5, 0.75, 1].map((t) => {
        const y = padTop + usableH * (1 - t);
        return (
          <line
            key={t}
            x1={padX}
            x2={width - padX}
            y1={y}
            y2={y}
            stroke="var(--gray-4)"
            strokeDasharray="3 3"
          />
        );
      })}
      {data.map((d, i) => {
        const h = (d.value / max) * usableH;
        const x = padX + i * (usableW / data.length) + 7;
        const y = padTop + usableH - h;
        return (
          <g key={d.label}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={6}
              fill="var(--orange-9)"
              opacity={d.value === 0 ? 0.15 : 0.95}
            >
              <title>{`${d.label}: ${formatCurrency(d.value)}`}</title>
            </rect>
            <text
              x={x + barW / 2}
              y={height - padBottom + 18}
              textAnchor="middle"
              fontSize="11"
              fill="var(--gray-10)"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// SVG donut chart
const Donut = ({ segments, total, centerLabel = 'Total' }) => {
  const size = 160;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--gray-4)" strokeWidth={stroke} />
        {segments.map((s, i) => {
          const frac = total > 0 ? s.value / total : 0;
          const len = frac * c;
          const circle = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={stroke}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
              style={{ transition: 'stroke-dasharray 0.6s ease' }}
            />
          );
          offset += len;
          return circle;
        })}
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            fontSize: '26px',
            fontWeight: 700,
            color: 'var(--gray-12)',
            lineHeight: 1,
          }}
        >
          {total}
        </span>
        <span style={{ fontSize: '11px', color: 'var(--gray-10)', marginTop: '2px' }}>{centerLabel}</span>
      </div>
    </div>
  );
};

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const profile = await providersAPI.getMyProfile();
        if (cancelled) return;
        setProvider(profile);

        try {
          const data = await bookingsAPI.getBookings({ providerId: profile.id }, { silent: true });
          if (cancelled) return;
          const list = Array.isArray(data) ? data : data?.bookings || [];
          setBookings(list);
        } catch { /* ignore */ }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        toast.error('Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const refetch = async () => {
    try {
      const profile = await providersAPI.getMyProfile();
      setProvider(profile);
      const data = await bookingsAPI.getBookings({ providerId: profile.id }, { silent: true });
      setBookings(Array.isArray(data) ? data : data?.bookings || []);
    } catch { /* ignore */ }
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      const pid = provider?.id;
      if (action === 'confirm') {
        await bookingsAPI.confirmBooking(bookingId, pid);
        toast.success('Booking accepted');
      } else if (action === 'decline') {
        const reason = window.prompt('Reason for declining (optional):');
        if (reason === null) return;
        await bookingsAPI.declineBooking(bookingId, reason, pid);
        toast.success('Booking declined');
      } else if (action === 'complete') {
        await bookingsAPI.completeBooking(bookingId, pid);
        toast.success('Booking marked as complete');
      }
      refetch();
    } catch (error) {
      console.error('Booking action error:', error);
    }
  };

  const services = provider?.services || [];
  const reviews = provider?.reviews || [];
  const rating = provider?.rating || 0;
  const totalReviews = provider?.totalReviews || reviews.length || 0;

  // Profile completeness
  const profileFields = [
    provider?.businessName, provider?.description, provider?.phone,
    provider?.location, provider?.photoUrl,
    provider?.languages?.length > 0, provider?.specializations?.length > 0,
    services.length > 0, provider?.availability?.length > 0,
  ];
  const profileComplete = profileFields.filter(Boolean).length;
  const profilePct = Math.round((profileComplete / profileFields.length) * 100);

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);

    const upcoming = bookings.filter((b) => {
      if (!UPCOMING_STATUSES.has(b.status)) return false;
      const d = bookingDate(b);
      return d ? isAfter(d, now) || isSameMonth(d, now) : true;
    });
    const completed = bookings.filter((b) => COMPLETED_STATUSES.has(b.status));
    const cancelled = bookings.filter((b) => CANCELLED_STATUSES.has(b.status));
    const pending = bookings.filter((b) => b.status === 'PENDING');

    const totalEarned = completed.reduce((acc, b) => acc + earningsAmount(b), 0);
    const monthEarned = completed.reduce((acc, b) => {
      const d = bookingDate(b);
      return d && isAfter(d, monthStart) ? acc + earningsAmount(b) : acc;
    }, 0);

    // next upcoming appointment
    const upcomingSorted = [...upcoming]
      .map((b) => ({ b, d: bookingDate(b) }))
      .filter((x) => x.d && isAfter(x.d, now))
      .sort((a, b) => a.d - b.d);
    const nextAppt = upcomingSorted[0]?.b || null;

    // monthly earnings (last 6 months)
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const m = subMonths(monthStart, i);
      months.push({ key: format(m, 'yyyy-MM'), label: format(m, 'MMM'), value: 0 });
    }
    completed.forEach((b) => {
      const d = bookingDate(b);
      if (!d) return;
      const key = format(d, 'yyyy-MM');
      const slot = months.find((m) => m.key === key);
      if (slot) slot.value += earningsAmount(b);
    });

    // Top services by booking count
    const serviceMap = {};
    services.forEach((s) => { serviceMap[s.id] = s.name; });
    const serviceCounts = {};
    bookings.forEach((b) => {
      const name = b.service?.name || serviceMap[b.serviceId] || b.serviceName;
      if (!name) return;
      serviceCounts[name] = (serviceCounts[name] || 0) + 1;
    });
    const topServices = Object.entries(serviceCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      upcoming, completed, cancelled, pending,
      totalEarned, monthEarned,
      nextAppt, months, topServices,
    };
  }, [bookings, services]);

  const donutSegments = useMemo(
    () => [
      { label: 'Completed', value: stats.completed.length, color: STATUS_COLORS.Completed },
      { label: 'Upcoming', value: stats.upcoming.length, color: 'var(--orange-9)' },
      { label: 'Cancelled', value: stats.cancelled.length, color: STATUS_COLORS.Cancelled },
    ],
    [stats]
  );
  const donutTotal = donutSegments.reduce((a, s) => a + s.value, 0);

  const recentActivity = useMemo(
    () =>
      [...bookings]
        .map((b) => ({ b, d: bookingDate(b) }))
        .filter((x) => x.d)
        .sort((a, b) => b.d - a.d)
        .slice(0, 5)
        .map((x) => x.b),
    [bookings]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px' }}>

        {/* Header */}
        <div className="mb-8 text-center">
          <Link
            to="/provider/profile"
            title="Edit your business profile"
            style={{
              display: 'inline-block',
              marginBottom: '12px',
              textDecoration: 'none',
            }}
          >
            {provider?.photoUrl ? (
              <img
                src={provider.photoUrl}
                alt={provider?.businessName || 'Profile'}
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 0 0 2px var(--orange-6)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.04)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(240,90,53,0.25), 0 0 0 2px var(--orange-9)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08), 0 0 0 2px var(--orange-6)';
                }}
              />
            ) : (
              <div
                style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--orange-3)',
                  color: 'var(--orange-10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  fontWeight: 700,
                  border: '3px solid #fff',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 0 0 2px var(--orange-6)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                {(provider?.businessName || user?.name)?.charAt(0)?.toUpperCase() || 'P'}
              </div>
            )}
          </Link>
          <h1
            style={{
              fontSize: '28px',
              fontWeight: 700,
              margin: '0 0 4px',
              color: 'var(--gray-12)',
              textAlign: 'center',
            }}
          >
            {provider?.businessName || user?.name || 'Welcome'}
          </h1>
          <div className="flex items-center justify-center gap-3" style={{ marginTop: '4px' }}>
            {provider?.location && (
              <span className="flex items-center gap-1" style={{ fontSize: '13px', color: 'var(--gray-10)' }}>
                <MapPinIcon style={{ width: '14px', height: '14px' }} />
                {provider.location}
              </span>
            )}
            {provider?.isApproved ? (
              <span className="flex items-center gap-1" style={{ fontSize: '13px', color: '#22c55e', fontWeight: 500 }}>
                <CheckCircleIcon style={{ width: '14px', height: '14px' }} />
                Verified
              </span>
            ) : (
              <span style={{ fontSize: '13px', color: 'var(--orange-9)', fontWeight: 500 }}>
                {provider?.verificationStatus || 'Pending verification'}
              </span>
            )}
          </div>
          <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '8px 0 0', textAlign: 'center' }}>
            Here's a snapshot of your business performance.
          </p>
        </div>

        {/* Verification warning */}
        {!provider?.isApproved && (
          <Card
            style={{
              backgroundColor: provider?.verificationStatus === 'rejected' ? '#fff5f5' : 'var(--orange-2)',
              borderColor: provider?.verificationStatus === 'rejected' ? '#fca5a5' : 'var(--orange-5)',
              marginBottom: '16px',
              padding: '14px 18px',
            }}
          >
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon style={{
                width: '20px', height: '20px',
                color: provider?.verificationStatus === 'rejected' ? 'var(--red-9, #e5484d)' : 'var(--orange-9)',
                flexShrink: 0,
              }} />
              <p style={{ fontSize: '14px', color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                {provider?.verificationStatus === 'rejected'
                  ? `Application rejected: ${provider?.rejectionReason || 'Contact support for details.'}`
                  : 'Your account is under review. Some features are limited until approved.'}
              </p>
            </div>
          </Card>
        )}

        {/* Profile completeness */}
        {profilePct < 100 && (
          <Card style={{ marginBottom: '16px', padding: '16px 18px' }}>
            <div className="flex items-center gap-5">
              <div style={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
                <svg viewBox="0 0 36 36" style={{ width: '64px', height: '64px', transform: 'rotate(-90deg)' }}>
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--gray-4)" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.9" fill="none"
                    stroke="var(--orange-9)" strokeWidth="3"
                    strokeDasharray={`${profilePct} ${100 - profilePct}`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                  />
                </svg>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)' }}>
                    {profilePct}%
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                  Profile Completeness
                </p>
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '2px 0 0', textAlign: 'left' }}>
                  Complete your profile to attract more clients
                </p>
              </div>
              <Link
                to="/provider/profile"
                className="inline-flex items-center gap-2 text-sm font-semibold"
                style={{
                  color: 'var(--orange-10)',
                  textDecoration: 'none',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid var(--orange-6)',
                  backgroundColor: '#fff',
                  flexShrink: 0,
                }}
              >
                Edit Profile
                <ArrowRightIcon style={{ width: '14px', height: '14px' }} />
              </Link>
            </div>
          </Card>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4 mb-4 sm:mb-6">
          <StatCard
            label="Upcoming"
            value={stats.upcoming.length}
            sub={stats.pending.length > 0 ? `${stats.pending.length} awaiting your response` : 'Bookings ahead'}
            icon={CalendarDaysIcon}
            tint="var(--orange-3)"
            iconColor="var(--orange-9)"
          />
          <StatCard
            label="Completed"
            value={stats.completed.length}
            sub={`${stats.cancelled.length} cancelled`}
            icon={CheckCircleIcon}
            tint="#dcfce7"
            iconColor="#22c55e"
          />
          <StatCard
            label="Total Earned"
            value={formatCurrency(stats.totalEarned)}
            sub={`${formatCurrency(stats.monthEarned)} this month`}
            icon={CurrencyDollarIcon}
            tint="#dbeafe"
            iconColor="#3b82f6"
          />
          <StatCard
            label="Rating"
            value={rating > 0 ? rating.toFixed(1) : '—'}
            sub={totalReviews > 0 ? `${totalReviews} review${totalReviews !== 1 ? 's' : ''}` : 'No reviews yet'}
            icon={StarIcon}
            tint="#fef9c3"
            iconColor="#eab308"
          />
        </div>

        {/* Next appointment — compact one-liner */}
        {stats.nextAppt && (
          <Link
            to="/provider/bookings"
            className="flex items-center gap-2.5 mb-6"
            style={{
              background: 'linear-gradient(135deg, var(--orange-2), #fff)',
              border: '1px solid var(--orange-5)',
              borderRadius: '12px',
              padding: '10px 14px',
              textDecoration: 'none',
            }}
          >
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '8px',
                backgroundColor: 'var(--orange-9)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CalendarDaysIcon style={{ width: '15px', height: '15px' }} />
            </div>
            <p
              className="flex-1 min-w-0 truncate"
              style={{ fontSize: 'clamp(12px, 3vw, 14px)', color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}
            >
              <span style={{ fontWeight: 700, color: 'var(--orange-10)' }}>Next: </span>
              <span style={{ fontWeight: 600 }}>{stats.nextAppt.service?.name || 'Service'}</span>
              {bookingDate(stats.nextAppt) && (
                <span style={{ color: 'var(--gray-11)' }}>
                  {' '}· {format(bookingDate(stats.nextAppt), 'EEE, MMM d')}, {format(bookingDate(stats.nextAppt), 'p')}
                </span>
              )}
              <span style={{ color: 'var(--orange-10)', fontWeight: 600 }}>
                {' '}· {formatCurrency(earningsAmount(stats.nextAppt))}
              </span>
            </p>
            <ArrowRightIcon style={{ width: '15px', height: '15px', color: 'var(--orange-10)', flexShrink: 0 }} />
          </Link>
        )}

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <Card style={{ gridColumn: 'span 1 / span 1' }} className="lg:col-span-2">
            <SectionTitle icon={ArrowTrendingUpIcon}>Monthly Earnings</SectionTitle>
            {stats.months.some((m) => m.value > 0) ? (
              <EarningsChart data={stats.months} />
            ) : (
              <div
                style={{
                  height: '180px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--gray-10)',
                  fontSize: '13px',
                }}
              >
                No earnings yet — completed bookings will show here.
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle icon={ChartBarIcon}>Bookings Breakdown</SectionTitle>
            <div className="flex items-center gap-5">
              <Donut segments={donutSegments} total={donutTotal} centerLabel="Bookings" />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {donutSegments.map((s) => (
                  <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: s.color,
                        display: 'inline-block',
                      }}
                    />
                    <div>
                      <div style={{ fontSize: '13px', color: 'var(--gray-12)', fontWeight: 600, textAlign: 'left' }}>
                        {s.label}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--gray-10)', textAlign: 'left' }}>
                        {s.value} ({donutTotal > 0 ? Math.round((s.value / donutTotal) * 100) : 0}%)
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        </div>

        {/* Top services + Recent activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <SectionTitle
              icon={ScissorsIcon}
              action={
                <Link
                  to="/provider/services"
                  style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none' }}
                >
                  Manage
                </Link>
              }
            >
              Top Services
            </SectionTitle>
            {stats.topServices.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--gray-10)', padding: '8px 0' }}>
                No bookings yet.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {stats.topServices.map((s) => {
                  const pct = Math.round((s.count / stats.topServices[0].count) * 100);
                  return (
                    <li key={s.name}>
                      <div className="flex items-center justify-between" style={{ marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--gray-12)' }}>
                          {s.name}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                          {s.count} booking{s.count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div
                        style={{
                          height: '6px',
                          borderRadius: '3px',
                          backgroundColor: 'var(--gray-3)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            backgroundColor: 'var(--orange-9)',
                            transition: 'width 0.6s ease',
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card>
            <SectionTitle
              icon={ClockIcon}
              action={
                <Link
                  to="/provider/bookings"
                  style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none' }}
                >
                  View all
                </Link>
              }
            >
              Recent Activity
            </SectionTitle>
            {recentActivity.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--gray-10)', padding: '8px 0' }}>
                No recent activity.
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column' }}>
                {recentActivity.map((b, i) => {
                  const d = bookingDate(b);
                  const color =
                    COMPLETED_STATUSES.has(b.status) ? '#22c55e'
                    : UPCOMING_STATUSES.has(b.status) ? 'var(--orange-9)'
                    : CANCELLED_STATUSES.has(b.status) ? '#e5484d'
                    : 'var(--gray-9)';
                  return (
                    <li
                      key={b.id || i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '10px 0',
                        borderBottom: i < recentActivity.length - 1 ? '1px solid var(--gray-4)' : 'none',
                      }}
                    >
                      <Avatar src={b.client?.avatarUrl} name={b.client?.name} size={36} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)', textAlign: 'left' }}>
                          {b.service?.name || 'Service'}
                          {b.client?.name && (
                            <span style={{ color: 'var(--gray-10)', fontWeight: 400 }}>
                              {' '}· {b.client.name}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--gray-10)', textAlign: 'left' }}>
                          {d ? format(d, 'MMM d, yyyy · p') : '—'}
                        </div>
                      </div>
                      {b.status === 'PENDING' ? (
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={() => handleBookingAction(b.id, 'confirm')}
                            style={{
                              padding: '4px 10px', borderRadius: '6px', border: 'none',
                              backgroundColor: '#22c55e', color: '#fff',
                              fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleBookingAction(b.id, 'decline')}
                            style={{
                              padding: '4px 10px', borderRadius: '6px',
                              border: '1px solid var(--red-6, #fca5a5)',
                              backgroundColor: 'transparent', color: 'var(--red-9, #e5484d)',
                              fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Decline
                          </button>
                        </div>
                      ) : b.status === 'CONFIRMED' ? (
                        <button
                          onClick={() => handleBookingAction(b.id, 'complete')}
                          style={{
                            padding: '4px 10px', borderRadius: '6px', border: 'none',
                            backgroundColor: 'var(--orange-9)', color: '#fff',
                            fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        >
                          Complete
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 shrink-0">
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              color: color,
                              backgroundColor: `${color}20`,
                            }}
                          >
                            {b.status}
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-12)' }}>
                            {earningsAmount(b) > 0 ? formatCurrency(earningsAmount(b)) : ''}
                          </span>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        {/* Recent reviews */}
        {reviews.length > 0 && (
          <Card style={{ marginTop: '16px' }}>
            <SectionTitle
              icon={StarIcon}
              action={
                <Link
                  to="/provider/reviews"
                  style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textDecoration: 'none' }}
                >
                  View all
                </Link>
              }
            >
              Recent Reviews
            </SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {reviews.slice(0, 3).map((r) => (
                <div
                  key={r.id}
                  style={{
                    padding: '14px',
                    border: '1px solid var(--gray-4)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--gray-2)',
                  }}
                >
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <StarSolid
                        key={i}
                        style={{
                          width: '14px',
                          height: '14px',
                          color: i < (r.rating || 0) ? '#facc15' : 'var(--gray-5)',
                        }}
                      />
                    ))}
                  </div>
                  {r.comment && (
                    <p style={{ fontSize: '13px', color: 'var(--gray-11)', margin: 0, textAlign: 'left' }}>
                      "{r.comment.length > 120 ? `${r.comment.slice(0, 120).trim()}…` : r.comment}"
                    </p>
                  )}
                  {r.client?.name && (
                    <div
                      className="flex items-center gap-2"
                      style={{ marginTop: '10px', fontSize: '12px', color: 'var(--gray-10)' }}
                    >
                      <Avatar src={r.client?.avatarUrl} name={r.client?.name} size={20} fontSize="10px" />
                      {r.client.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProviderDashboard;
